import React, { useState } from 'react';
import { Layout, Tabs, Card, Typography, Collapse } from 'antd';
import { CodeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ApplicationsList from '../components/Logging/ApplicationsList';
import LogViewer from '../components/Logging/LogViewer';
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

const { Content } = Layout;
const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;

const IntegrationGuide = () => {
  const { t } = useTranslation();

  const codeBlockStyle = {
    background: '#1e1e1e',
    color: '#d4d4d4',
    padding: 16,
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'monospace',
    overflowX: 'auto',
    whiteSpace: 'pre',
    margin: '8px 0 16px'
  };

  const npmSnippet = `npm install quto-logger`;

  const usageSnippet = `import QutoLogger from 'quto-logger';

const logger = new QutoLogger({
  apiKey: 'YOUR_API_KEY',
  environment: 'production'
});

// Log errors, warnings, or info
await logger.error('Payment failed', { userId: '123' });
await logger.warning('Slow response', { latency: 2500 });
await logger.info('User signed up', { plan: 'pro' });

// Capture exceptions with stack traces
try {
  await riskyOperation();
} catch (err) {
  await logger.captureException(err);
}`;

  const curlSnippet = `curl -X POST https://api.quto.dev/api/logs \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "level": "error",
    "message": "Something went wrong",
    "stack": "Error: ...\\n    at main (app.js:10)",
    "environment": "production",
    "metadata": { "userId": "123" }
  }'`;

  return (
    <Collapse
      ghost
      items={[
        {
          key: 'guide',
          label: (
            <span style={{ fontWeight: 500, fontSize: 15 }}>
              <CodeOutlined style={{ marginRight: 8 }} />
              {t('integration_guide')}
            </span>
          ),
          children: (
            <div>
              <Paragraph type="secondary">{t('integration_guide_desc')}</Paragraph>

              <Text strong>{t('install_package')}</Text>
              <div style={codeBlockStyle}>{npmSnippet}</div>

              <Text strong>Usage:</Text>
              <div style={codeBlockStyle}>{usageSnippet}</div>

              <Text strong>{t('or_use_rest_api')}</Text>
              <div style={codeBlockStyle}>{curlSnippet}</div>
            </div>
          ),
        },
      ]}
    />
  );
};

const ErrorLogging = () => {
  const { t } = useTranslation();
  const [openApps, setOpenApps] = useState([]);
  const [activeTab, setActiveTab] = useState('applications');

  const handleSelectApplication = (app) => {
    const tabKey = `app-${app.id}`;
    setOpenApps((prev) => {
      if (prev.some((a) => a.id === app.id)) return prev;
      return [...prev, app];
    });
    setActiveTab(tabKey);
  };

  const handleTabEdit = (targetKey, action) => {
    if (action === 'remove') {
      setOpenApps((prev) => prev.filter((a) => `app-${a.id}` !== targetKey));
      if (activeTab === targetKey) {
        setActiveTab('applications');
      }
    }
  };

  return (
    <ProtectedToolWrapper>
      <div className="min-h-screen bg-gray-50">
        <Content style={{ padding: '24px 50px' }}>
          <div className="max-w-7xl mx-auto">
            <IntegrationGuide />
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="editable-card"
              hideAdd
              onEdit={handleTabEdit}
              style={{ marginTop: 16 }}
            >
              <TabPane tab={t('applications')} key="applications" closable={false}>
                <ApplicationsList onSelectApplication={handleSelectApplication} />
              </TabPane>
              {openApps.map((app) => (
                <TabPane tab={`${app.name} - ${t('logs')}`} key={`app-${app.id}`}>
                  <LogViewer application={app} />
                </TabPane>
              ))}
            </Tabs>
          </div>
        </Content>
      </div>
    </ProtectedToolWrapper>
  );
};

export default ErrorLogging;